import React, {useState} from "react"
import {useAsyncResult} from "../../../hooks/useAsyncResult"
import {getAccountLimits, getAccountDepositProducts, LimitOverrideType} from "../../../resources/account"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import {MainSection} from "../../../containers/MainSection/MainSection"
import {LimitOverrideModal} from "./LimitOverrideModal"
import {useRefreshToken} from "../../../hooks/useRefreshToken"
import {LimitsPageDescription} from "./LimitsPageBlockHeader"
import {getLimitValueByType, getExistingOverrideFromLimits} from "./helpers"
import {AllLimitsSections} from "./sections"
import {AccountLimitsPagePending} from "./AccountLimitsPagePending"
import {hasPermission} from "../../../services/permission"
import {Link} from "react-router-dom"

interface ModalState {
	isOpen: boolean
	limitType: LimitOverrideType | null
}

export default function AccountLimitsPage({
	accountId,
	accessToken,
	depositProduct,
}: {
	accountId: string
	accessToken: string
	depositProduct?: string
}) {
	const [refreshToken, refresh] = useRefreshToken()
	const accountLimits = useAsyncResult(() => getAccountLimits(accessToken, accountId), [refreshToken])
	const canUpdateOverrides = hasPermission("accountConfigOverride.limits", "update")
	const depositProductsResult = useAsyncResult(() => getAccountDepositProducts(accessToken, accountId), [accountId])
	const depositProductId = depositProductsResult.match(
		() => undefined,
		(res) => res.data.find((dp) => dp.attributes.name === depositProduct)?.id,
		() => undefined
	)

	const [modalState, setModalState] = useState<ModalState>({isOpen: false, limitType: null})

	const handleEditLimit = canUpdateOverrides
		? (limitType: LimitOverrideType) => {
				setModalState({isOpen: true, limitType})
		  }
		: undefined

	const handleCloseModal = () => {
		setModalState({isOpen: false, limitType: null})
	}

	return (
		<AsyncResultComponent asyncResult={accountLimits} pendingComponent={<AccountLimitsPagePending />}>
			{({value: limits}) => {
				const depositProductLimit = modalState.limitType ? getLimitValueByType(limits, modalState.limitType) : 0

				const existingOverrideInfo = modalState.limitType
					? getExistingOverrideFromLimits(limits, modalState.limitType)
					: undefined

				const currentLimit = existingOverrideInfo?.amount ?? depositProductLimit

				const existingOverride = existingOverrideInfo
					? {
							amount: existingOverrideInfo.amount,
							expiredAt: existingOverrideInfo.expiredAt,
							overrideId: "",
					  }
					: undefined

				return (
					<MainSection>
						<div className="account-limits-page">
							<div className="account-limits-page-header">
								<div>
									<h2 className="account-limits-page-title">Account Limits</h2>
									<LimitsPageDescription />
								</div>
								{depositProductId && (
									<Link
										to={`/deposit-products/${depositProductId}`}
										className="account-limits-page-deposit-product-link"
									>
										See Deposit Product
									</Link>
								)}
							</div>

							<AllLimitsSections limits={limits.attributes} onEditLimit={handleEditLimit} />
						</div>

						{canUpdateOverrides && modalState.isOpen && modalState.limitType && (
							<LimitOverrideModal
								close={handleCloseModal}
								limitType={modalState.limitType}
								currentLimit={currentLimit}
								depositProductLimit={depositProductLimit}
								existingOverride={existingOverride}
								accountId={accountId}
								onSuccess={() => refresh()}
							/>
						)}
					</MainSection>
				)
			}}
		</AsyncResultComponent>
	)
}
